/**
 * Presentation formatting for the UI. Pure functions, no React.
 *
 * Amounts arrive as integer öre and leave as Swedish strings: no-break space
 * as thousands separator, decimal comma, U+2212 for negatives, a thin space
 * before "kr". Dates are ISO strings in, Swedish prose out.
 */
import type { Ore } from '@/domain/money';
import type { IsoDate, IsoDateTime } from '@/domain/types';

/** No-break space, used between digit groups so "1 234" never wraps. */
export const NBSP = ' ';
/** Thin space between the number and "kr" (the `.amount` class is nowrap). */
export const THIN_SPACE = ' ';
/** Typographic minus sign. */
export const MINUS = '−';

export interface SekParts {
  negative: boolean;
  /** Integer kronor with no-break-space thousands separators, no sign. */
  whole: string;
  /** Two öre digits. */
  fraction: string;
}

/** Split öre into display parts so the öre can be styled smaller. */
export function splitSek(amount: Ore): SekParts {
  if (!Number.isFinite(amount)) throw new RangeError('Amount must be a finite number of öre');
  const rounded = Math.round(amount);
  const negative = rounded < 0;
  const abs = Math.abs(rounded);
  const kronor = Math.floor(abs / 100);
  const ore = abs % 100;
  return { negative, whole: groupThousands(kronor), fraction: String(ore).padStart(2, '0') };
}

function groupThousands(n: number): string {
  const digits = String(n);
  let out = '';
  for (let i = 0; i < digits.length; i++) {
    const fromEnd = digits.length - i;
    out += digits[i];
    if (fromEnd > 1 && fromEnd % 3 === 1) out += NBSP;
  }
  return out;
}

export interface FormatSekOptions {
  /** Append " kr" (thin space). Default true. */
  unit?: boolean;
  /** Drop ",00" when the amount is whole kronor. Default false. */
  trimZeroOre?: boolean;
  /** Prefix "+" for positive amounts (e.g. deltas). Default false. */
  signed?: boolean;
}

/** "1 234,50 kr", "−625,00 kr". */
export function formatSek(amount: Ore, opts: FormatSekOptions = {}): string {
  const { negative, whole, fraction } = splitSek(amount);
  const unit = opts.unit ?? true;
  const showFraction = !(opts.trimZeroOre && fraction === '00');
  const sign = negative ? MINUS : opts.signed && amount > 0 ? '+' : '';
  return `${sign}${whole}${showFraction ? `,${fraction}` : ''}${unit ? `${THIN_SPACE}kr` : ''}`;
}

/** Whole kronor, no öre, no unit: "4 250". Used for Skatteverket boxes. */
export function formatKronor(amount: Ore): string {
  const kronor = Math.trunc(amount / 100);
  return `${kronor < 0 ? MINUS : ''}${groupThousands(Math.abs(kronor))}`;
}

/** "ca 4 250 kr" rounded to whole kronor, for sentences on Hem. */
export function formatSekApprox(amount: Ore): string {
  const kronor = Math.round(Math.abs(amount) / 100);
  return `ca${NBSP}${amount < 0 ? MINUS : ''}${groupThousands(kronor)}${THIN_SPACE}kr`;
}

// ---- Dates ------------------------------------------------------------------

export const MONTHS_SV: readonly string[] = [
  'januari',
  'februari',
  'mars',
  'april',
  'maj',
  'juni',
  'juli',
  'augusti',
  'september',
  'oktober',
  'november',
  'december',
];

export const MONTHS_SHORT_SV: readonly string[] = ['jan', 'feb', 'mars', 'apr', 'maj', 'juni', 'juli', 'aug', 'sep', 'okt', 'nov', 'dec'];

export const WEEKDAYS_SV: readonly string[] = ['söndag', 'måndag', 'tisdag', 'onsdag', 'torsdag', 'fredag', 'lördag'];

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})/;

export function parseIso(date: IsoDate | IsoDateTime): { year: number; month: number; day: number } {
  const m = ISO_DATE.exec(date);
  if (!m) throw new RangeError(`Not an ISO date: ${date}`);
  return { year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) };
}

export function isIsoDate(value: string): value is IsoDate {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const { year, month, day } = parseIso(value);
  if (month < 1 || month > 12 || day < 1) return false;
  return day <= new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** Today's local date as ISO. */
export function todayIso(now: Date = new Date()): IsoDate {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function dayNumber(date: IsoDate): number {
  const { year, month, day } = parseIso(date);
  return Math.round(Date.UTC(year, month - 1, day) / 86_400_000);
}

/** Whole days from `from` to `to`; negative when `to` is earlier. */
export function daysBetween(from: IsoDate, to: IsoDate): number {
  return dayNumber(to) - dayNumber(from);
}

/** "4 mars 2026". */
export function formatDate(date: IsoDate | IsoDateTime): string {
  const { year, month, day } = parseIso(date);
  return `${day} ${MONTHS_SV[month - 1]} ${year}`;
}

/** "onsdag 4 mars 2026". */
export function formatDateLong(date: IsoDate | IsoDateTime): string {
  const { year, month, day } = parseIso(date);
  const weekday = WEEKDAYS_SV[new Date(Date.UTC(year, month - 1, day)).getUTCDay()];
  return `${weekday} ${formatDate(date)}`;
}

/** "4 mars", or "4 mars 2025" when the year differs from `relativeTo` (default: today). */
export function formatDateShort(date: IsoDate | IsoDateTime, relativeTo: IsoDate = todayIso()): string {
  const { year, month, day } = parseIso(date);
  const base = `${day} ${MONTHS_SV[month - 1]}`;
  return parseIso(relativeTo).year === year ? base : `${base} ${year}`;
}

/** "4 mars" using the abbreviation used in running text ("1 okt", "12 nov"). */
export function formatDateAbbrev(date: IsoDate | IsoDateTime, relativeTo: IsoDate = todayIso()): string {
  const { year, month, day } = parseIso(date);
  const base = `${day} ${MONTHS_SHORT_SV[month - 1]}`;
  return parseIso(relativeTo).year === year ? base : `${base} ${year}`;
}

/**
 * Relative day words for sentences like "Senast Clas Ohlson, i går".
 * i dag · i går · i förrgår · för 3 dagar sedan (≤ 6) · i morgon · om 3 dagar
 * (≤ 6) · otherwise "4 mars" (year appended when it differs).
 */
export function formatRelativeDate(date: IsoDate | IsoDateTime, today: IsoDate = todayIso()): string {
  const diff = daysBetween(today, date.slice(0, 10));
  if (diff === 0) return 'i dag';
  if (diff === -1) return 'i går';
  if (diff === -2) return 'i förrgår';
  if (diff === 1) return 'i morgon';
  if (diff < 0 && diff >= -6) return `för ${-diff} dagar sedan`;
  if (diff > 1 && diff <= 6) return `om ${diff} dagar`;
  return formatDateShort(date, today);
}

/** "om 18 dagar", "i dag", "i morgon", "för 2 dagar sedan" (also beyond a week). */
export function formatDaysUntil(deadline: IsoDate, today: IsoDate = todayIso()): string {
  const diff = daysBetween(today, deadline);
  if (diff === 0) return 'i dag';
  if (diff === 1) return 'i morgon';
  if (diff === -1) return 'i går';
  if (diff > 1) return `om ${diff} dagar`;
  return `för ${-diff} dagar sedan`;
}

/** "14:02" from an ISO timestamp, in local time. */
export function formatTime(at: IsoDateTime): string {
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return '';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** "i dag 14:02" · "i går 09:14" · "4 mars 14:02". */
export function formatDateTime(at: IsoDateTime, today: IsoDate = todayIso()): string {
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return '';
  const local = todayIso(d);
  const diff = daysBetween(today, local);
  const day = diff === 0 ? 'i dag' : diff === -1 ? 'i går' : formatDateShort(local, today);
  return `${day} ${formatTime(at)}`;
}

/** "4 mars – 12 mars 2026" / "1 jan – 31 dec 2026" / "30 dec 2025 – 3 jan 2026". */
export function formatDateRange(from: IsoDate, to: IsoDate): string {
  const a = parseIso(from);
  const b = parseIso(to);
  const left = `${a.day} ${MONTHS_SHORT_SV[a.month - 1]}${a.year === b.year ? '' : ` ${a.year}`}`;
  const right = `${b.day} ${MONTHS_SHORT_SV[b.month - 1]} ${b.year}`;
  return `${left} – ${right}`;
}

// ---- Periods ---------------------------------------------------------------

/**
 * Period keys as sentences: 'YYYY-MM' → "mars 2026"; 'YYYY-Qn' → "kvartal 3 2026";
 * 'FY:<id>' → "räkenskapsåret <label>" when the label is known, else "räkenskapsåret".
 */
export function formatPeriodKey(key: string, fiscalYearLabel?: string): string {
  const month = /^(\d{4})-(\d{2})$/.exec(key);
  if (month) {
    const m = Number(month[2]);
    if (m >= 1 && m <= 12) return `${MONTHS_SV[m - 1]} ${month[1]}`;
  }
  const quarter = /^(\d{4})-Q([1-4])$/.exec(key);
  if (quarter) return `kvartal ${quarter[2]} ${quarter[1]}`;
  if (key.startsWith('FY:')) return fiscalYearLabel ? `räkenskapsåret ${fiscalYearLabel}` : 'räkenskapsåret';
  return key;
}

/** Capitalise the first letter ("kvartal 3 2026" → "Kvartal 3 2026"). */
export function capitalize(s: string): string {
  return s.length ? s[0]!.toLocaleUpperCase('sv-SE') + s.slice(1) : s;
}

// ---- Misc ---------------------------------------------------------------------

/** "1 kvitto" / "3 kvitton". */
export function plural(n: number, singular: string, pluralForm: string): string {
  return `${groupThousands(Math.abs(n))} ${n === 1 ? singular : pluralForm}`;
}

/** "556000-0000" from digits; other lengths are returned as typed. */
export function formatOrgNumber(digits: string): string {
  const d = digits.replace(/\D/g, '');
  if (d.length === 10) return `${d.slice(0, 6)}-${d.slice(6)}`;
  if (d.length === 12) return `${d.slice(2, 8)}-${d.slice(8)}`;
  return digits;
}

/** "A12" from a series and a number. */
export function formatVerNumber(series: string, number: number): string {
  return `${series}${number}`;
}

/** "25 %" with a no-break space. */
export function formatPercent(value: number): string {
  return `${String(value).replace('.', ',')}${NBSP}%`;
}

/** "312 kB" / "2,4 MB". */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes}${NBSP}B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)}${NBSP}kB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')}${NBSP}MB`;
}

/** Confidence (0–1) to whole percent. */
export function formatConfidence(c: number): string {
  return formatPercent(Math.round(Math.max(0, Math.min(1, c)) * 100));
}
